import type { AUTO_NOTE_STRATEGIES } from 'constants/hour';
import type { EnrichedForecast } from 'routes/forecast/types/forecast';
import type { Currency } from 'types/account';
import type { Client } from 'types/client';
import type { EnrichedProject } from 'types/project';
import type { Suggestion } from 'types/suggestion';
import type { MemoryGroup, SuggestedEntryProject } from 'types/timeline';
import type { Cost, Duration } from 'types/units';
import type { User } from 'types/user';
import type { HourAnalyticsContext, HourAnalyticsSaveContext } from './analytics';
import type { TagBase } from './tag';
import type { Merge } from './utility';

type TimerState = 'default' | 'start' | 'stop';
export type SetTimerState = false | 'start' | 'stop';
export type AutoNoteStrategy = (typeof AUTO_NOTE_STRATEGIES)[number];

export enum HourLockedReasons {
  Invoiced = 'invoiced',
  Billed = 'billed',
  InvalidPermission = 'invalid_permission',
  Locked = 'locked',
}

export interface HourTimestamp {
  id?: number;
  from: string;
  to: string;
  /** note to self: Was optional, (temporarily?) made required to help identify flaws */
  entry_ids: Array<string | number>;
}

import type { HourState } from 'redux/modules/hourStates.schema';

export interface Hour<T> {
  id: number;
  uid?: string;
  note: string;
  noteFromMemories?: string;
  day: string;
  cost: Cost;
  estimated_cost: Cost;
  internal_cost: Cost;
  profit: Cost;
  profitability: number | undefined;
  project: T;
  user: User;
  duration: Duration;
  estimated_duration: Duration;
  hours: number;
  minutes: number;
  seconds: number;
  sequence: number;
  estimated: boolean;
  timer_state: TimerState;
  timer_started_on: number;
  timer_stopped_on: number;
  updated_at: number;
  created_at: number;
  billed: boolean;
  billable: boolean;
  to: string | null;
  from: string | null;
  timestamps: Array<HourTimestamp>;
  deleted: boolean;
  hour_rate?: number;
  hour_rate_in_cents?: number;
  creator_id?: number;
  suggester_id?: number;
  updater_id?: number;
  external_id?: string | null;
  label_ids: Array<number>;
  required_label_ids: Array<number>;
  suggestion_id: number | null | undefined;
  user_ids: Array<number>;
  entry_ids: Array<number>;
  /**
   * Link hour to external references
   */
  external_links: Array<ExternalHourLink>;
  manage: boolean;
  locked: boolean;
  locked_reason: HourLockedReasons | null;
  invoice_id: number | null;
  /** Association to a forecast */
  forecast_id: number | null;
  /** When set, will mark the associated forecast_id as completed */
  forecast_completed_at?: string;
  /** Did we create this entry on the client-side before receiving a response from the backend? */
  _localDraft?: boolean;
  /** If true, this hour is a draft only visible to the current user. */
  draft?: boolean;
  state: HourState | null;
  /**
   * AutoSheet proposal this hour was submitted from. Absent (the API sends null) for hours
   * logged anywhere else. Typed like HourDraft's so an hour still seeds a draft.
   */
  autosheet_proposal_id?: string;

  _lastDirectlySelectedMemory?: {
    id: number;
    timestamp: string;
  };
}

type HourDraftOmittedAttributes = 'id' | 'user' | 'project';
export interface HourDraft extends Omit<Hour<number>, HourDraftOmittedAttributes> {
  id: number | null;
  project: number | null;
  user: { id: number };
  _unlockedByUser?: boolean;
  _suggestedNote?: string;
  /** In-flight/AI currently processing note generation request */
  _isGeneratingNote?: boolean;
  /**
   * When notable changes are made via `updateHourDraft`, we ALLOW note to be re-generated next time AutofillBar is clicked.
   * @see updateHourDraft & hourUtils.shouldRegenerateNoteForDraft
   */
  _shouldRegenerateNote?: boolean;
  _pusherUpdatedAt?: string;
  source?: Suggestion['source'];
  context: HourAnalyticsContext;
  _currency?: Currency;
  /** AutoSheet proposal UUID this hour was generated from. Lets the backend key revert on `delete_by_autosheet_proposal_id`. */
  autosheet_proposal_id?: string;
  /** Temporarily disables project client focus for this editor draft only. */
  _showAllProjectsForDraft?: boolean;
  /**
   * When ALL_PROJECTS_LOGGING is enabled, the picker shows only the user's
   * member projects by default. This flag (per editor draft, off by default)
   * expands the picker to include every active workspace project. Resets each
   * time the editor opens because a fresh draft is created per open.
   */
  _includeAllWorkspaceProjects?: boolean;
  /**
   * Tag ids this draft's *current* linked-work selection put on `label_ids` (TT-5623).
   *
   * Provenance, not state the user can edit: it records only the ids `resolveLinkedWorkTagIds`
   * actually added, so the next linked-work change can take exactly those back off again and
   * leave a tag the user picked by hand — even one the provider happens to carry too — in place.
   * Editor-local; a fresh draft starts without it and `normalizeHourToApi` never sends it.
   */
  _linkedWorkLabelIds?: Array<number>;
}

export type EnrichedHourDraft = Merge<HourDraft, { project: EnrichedProject }>;

// Saves to API:
export interface NormalizedHourToApi {
  id?: number;
  day: string;
  note: string;
  timer_state: string;
  timer_started_on: number;
  timer_stopped_on: number;
  suggestion_id?: number;
  forecast_id?: number | null;
  /** Completes the forecast if set to a datetime */
  forecast_completed_at?: string;
  project_id: number;
  user_id?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
  estimated_hours: number;
  estimated_minutes: number;
  entry_ids: Array<number>;
  user_ids: Array<number>;
  label_ids: Array<number>;
  from: string | null;
  to: string | null;
  timestamps?: Array<HourTimestamp>;
  sequence: number;
  billable?: boolean;
  billed?: boolean;
  locked?: boolean;
  locked_reason?: HourLockedReasons | null;
  context: HourAnalyticsSaveContext;
  state_id?: number | null;
  external_links: Array<ExternalHourLink>;
  /**
   * Native linked work item ids (full-replace semantics: an empty array clears them).
   * Native picks travel here INSTEAD of as an `external_links` entry; TIC picks are
   * unaffected. See `splitExternalLinksForSave`.
   */
  linked_work_item_ids?: Array<number>;
  /** AutoSheet proposal UUID this hour was generated from (sent only for AutoSheet-created hours). */
  autosheet_proposal_id?: string;
  /**
   * Under ALL_PROJECTS_LOGGING, controls what happens when the user logs to a
   * project they are not a member of. `"add"` permanently joins them (the
   * pre-existing behaviour, and the backend default when the field is absent);
   * `"log_once"` logs the time without leaving them as a member. Omitted when
   * the user is already a member of the picked project.
   */
  project_join_mode?: ProjectJoinMode;
}

/** @see NormalizedHourToApi.project_join_mode */
export type ProjectJoinMode = 'add' | 'log_once';

export interface HourDraftStatusMessage {
  key: string | number;
  type: 'default' | 'info' | 'success' | 'warning' | 'error';
  title: string;
  message?: string;
  avatar?: string | null;
  icon?: string | null;
  actions?: Array<HourDraftStatusMessageAction>;
}
export interface HourDraftStatusMessageAction {
  key: string | number;
  action: 'onUnlock' | 'onHide';
  label: string;
}
export type HourDraftStatusMessages = Array<HourDraftStatusMessage>;

export interface HourEditorProjectSectionProps {
  projects: Array<EnrichedProject>;
  pinnedProjects: Array<EnrichedProject>;
  recentProjects: Array<EnrichedProject>;
  suggestions: Array<SuggestedEntryProject>;
  clients: Array<Client>;
  forecasts: Array<EnrichedForecast>;
  currency: Currency;
  userId: number;
  ui: {
    isActingAsOtherUser: boolean;
    showForecastSuggestions?: boolean; // Remove optional when PlanningProjectSection is removed
    /** How many forecast/task suggestions do we show? */
    numForecastSuggestions: number;
    showProjectSuggestions?: boolean; // Remove optional when PlanningProjectSection is removed
    /** How many project suggestions do we show? */
    numProjectSuggestions: number;
    selectedProjectId: number | null;
    allowCreateProject: boolean;
    isLoading: boolean;
    placeholder: string;
    allProjectsLoaded: boolean;
    clientFocusOnProjects: HourEditorClientFocusOnProjects;
  };
}

export interface HourEditorClientFocusOnProjects {
  isAvailable: boolean;
  isEnabled: boolean;
  isActive: boolean;
  clientId: number | null;
  menuTooltip: string;
  notificationMessage: string | null;
}

export type HourEditorSuggestedDurations = Array<{
  key: string | number;
  seconds: number;
  isActive: boolean;
  isBasedOnMemories?: boolean;
}>;

export interface HourAsCard {
  type: 'hour';
  id: number;
  user_id: number;
  day: string;
  from: string | null;
  to: string | null;
  timestamps: Array<HourTimestamp>;
  sequence: number;
  note: string;
  numLoggedSecondsExact: number; // for countSecondsSince adjustment,
  numLoggedSecondsRoundedDown: number;
  numPlannedSeconds: number;
  numLoggedMoney: number; // for countSecondsSince adjustment,
  numPlannedMoney: number;
  isBilled: boolean;
  isBillable: boolean;
  isLocked: boolean;
  isTimerRunning: boolean;
  isInvoiced: boolean;
  isForecast: boolean;
  forecastId: number | null;
  invoiceId: number | null;
  externalId: string | null | undefined;
  isMemoryBased: boolean;
  timerRunningSince: number; // Adjust  plannedMinutes/loggedMoney based on this at lowest component level & re-render every 1000ms,
  loggedDurationMemoriesMismatch: boolean;
  numCalculatedSeconds: number;
  projectId: number;
  projectName: string;
  projectColor: string;
  projectCurrency: Currency;
  clientId: number;
  clientName: string;
  costPerSecond: number;
  entryIds: Array<number>;
  memoryGroups: Array<MemoryGroup>;
  cacheBuster: null | string;
  tags: Array<TagBase>;
  manage: boolean;
  stateId: number | null;
  ui: HourAsCardUI;
}

export interface HourAsCardUI {
  isSuggestion: boolean;
  isBasedOnSuggestion: boolean;
  isEditing: boolean;
  isSelected?: boolean;
  isSelectionMode?: boolean;
  hasNoNote: boolean;
  isContrastReadable: boolean;
  showLogged: boolean;
  hasPlannedTime: boolean;
  hasHourStatesFeature: boolean;
}

export interface MoveOrCopyHourPayload {
  dropEffect?: 'move' | 'copy';
  targetType: 'hour' | 'day';
  targetHour?: HourAsCard;
  sourceHour: HourAsCard;
  date: string;
  userId?: number;
  updateOptimistically?: boolean;
}

export const hourDragIdentifier = 'HOUR' as const;
export const forecastDragIdentifier = 'FORECAST' as const;

export interface ExternalHourLink {
  /** Name of the TIC provider, eg 'jira'. 'timely' for native linked work items */
  provider_type: string;
  /** Reference key, eg 'TIC-123'. Null for providers without human-readable codes (e.g. Asana) */
  provider_id: string | null;
  /** External id, eg 12345678 */
  external_id?: string;
  /**
   * Id of the native (Rails-backed) linked work item, present when provider_type is 'timely'.
   * The backend emits these entries with the `timely://work-items/<id>` uri included; the
   * client still mints it when absent (see `normalizeIncomingExternalLinks`) as deploy-skew
   * protection. On save, native links travel as `linked_work_item_ids` — native entries
   * echoed in `external_links` are ignored server-side.
   */
  linked_work_item_id?: number | null;
  /** TIC resource URI, or the `timely://work-items/<id>` uri for native linked work items */
  uri: string;
  /** HTML URL from metadata */
  html_url?: string;
  /** External code from metadata, eg 'TIC-123' */
  external_code?: string;
  /** Title from metadata */
  title?: string;
  /** Status from metadata. Always null for native linked work items */
  status?: string | null;
  /** Client mapped from the linked issue's Jira custom field (e.g. Beneficiary), from metadata */
  jira_custom_field?: string;
}
